import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { userService } from '../services/user';
import { Alert, Button, Card, Input } from '../components/ui';

export function Profile() {
  const { user, refresh } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cohort, setCohort] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(user?.first_name ?? '');
    setLastName(user?.last_name ?? '');
    setCohort(user?.cohort ?? '');
  }, [user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await userService.updateProfile({
        first_name: firstName,
        last_name: lastName,
        cohort,
      });
      await refresh();
      setSuccess('Profile updated.');
    } catch (err: any) {
      setError(err?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 bg-gray-900 min-h-screen text-white">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Profile</h1>
          <p className="text-gray-400 mt-1">
            Your name and cohort appear in instructor views and learner reports.
          </p>
        </div>

        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card className="p-6 bg-gray-800 border-gray-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="First name"
                value={firstName}
                maxLength={100}
                onChange={(event) => setFirstName(event.target.value)}
              />
              <Input
                label="Last name"
                value={lastName}
                maxLength={100}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>

            <Input
              label="Cohort"
              value={cohort}
              maxLength={100}
              onChange={(event) => setCohort(event.target.value)}
              helpText="Use the class, team, or training group your instructor expects."
            />

            <div className="grid gap-4 md:grid-cols-3 text-sm">
              <div>
                <div className="text-gray-500">Username</div>
                <div className="mt-1 text-gray-200">{user?.username}</div>
              </div>
              <div>
                <div className="text-gray-500">Email</div>
                <div className="mt-1 text-gray-200 break-all">{user?.email}</div>
              </div>
              <div>
                <div className="text-gray-500">Role</div>
                <div className="mt-1 text-gray-200 capitalize">{user?.role}</div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" loading={saving}>
                Save Profile
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
